import { api } from "./api";

let scriptPromise = null;

export function loadRazorpay() {
  if (window.Razorpay) return Promise.resolve(true);
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve) => {
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
  return scriptPromise;
}

/**
 * Starts the Razorpay checkout for a plan.
 * Returns { credits, plan, added } on success.
 * Throws an Error with a friendly message on failure.
 */
export async function purchasePlan(planId) {
  const { data: order } = await api.post("/payments/create-order", { plan: planId });

  const ok = await loadRazorpay();
  if (!ok) throw new Error("Could not load the payment gateway. Please try again.");

  return new Promise((resolve, reject) => {
    const rzp = new window.Razorpay({
      key: order.key_id,
      amount: order.amount,
      currency: order.currency,
      order_id: order.order_id,
      name: "The Pataudi",
      description: `${order.plan.label} Plan — ${order.plan.credits} contact unlocks`,
      prefill: { name: order.user?.name || "", email: order.user?.email || "", contact: order.user?.contact || "" },
      theme: { color: "#1B4F72" },
      method: { upi: true, card: true, netbanking: true, wallet: true },
      handler: async (res) => {
        try {
          const { data } = await api.post("/payments/verify", {
            razorpay_order_id: res.razorpay_order_id,
            razorpay_payment_id: res.razorpay_payment_id,
            razorpay_signature: res.razorpay_signature,
          });
          resolve(data);
        } catch (e) {
          reject(new Error(e.response?.data?.detail || "Payment verification failed."));
        }
      },
      modal: { ondismiss: () => reject(new Error("CANCELLED")) },
    });
    rzp.on("payment.failed", (resp) => reject(new Error(resp.error?.description || "Payment failed.")));
    rzp.open();
  });
}

/**
 * Purchases the per-listing Verified Badge (₹249) during the Step-3 wizard.
 * Returns { order_id } on success — pass this into the create-listing call
 * inside `keyDetails.__verified_order__` so the backend attaches the badge
 * to that specific listing.
 */
export async function purchaseListingVerifiedBadge() {
  const { data: order } = await api.post("/payments/listing-verified/create-order");
  const ok = await loadRazorpay();
  if (!ok) throw new Error("Could not load the payment gateway. Please try again.");

  return new Promise((resolve, reject) => {
    const rzp = new window.Razorpay({
      key: order.key_id,
      amount: order.amount,
      currency: order.currency,
      order_id: order.order_id,
      name: "The Pataudi",
      description: "Verified Badge for this listing (₹249, one-time)",
      prefill: { name: order.user?.name || "", email: order.user?.email || "", contact: order.user?.contact || "" },
      theme: { color: "#B8860B" },
      method: { upi: true, card: true, netbanking: true, wallet: true },
      handler: async (res) => {
        try {
          const { data } = await api.post("/payments/listing-verified/verify", {
            razorpay_order_id: res.razorpay_order_id,
            razorpay_payment_id: res.razorpay_payment_id,
            razorpay_signature: res.razorpay_signature,
          });
          resolve({ order_id: data.order_id || res.razorpay_order_id });
        } catch (e) {
          reject(new Error(e.response?.data?.detail || "Payment verification failed."));
        }
      },
      modal: { ondismiss: () => reject(new Error("CANCELLED")) },
    });
    rzp.on("payment.failed", (resp) => reject(new Error(resp.error?.description || "Payment failed.")));
    rzp.open();
  });
}

/**
 * Purchases the Verified Seller badge (₹249, one-time lifetime).
 * Returns { isVerifiedSeller: true } on success.
 */
export async function purchaseVerifiedBadge() {
  const { data: order } = await api.post("/payments/verified/create-order");
  const ok = await loadRazorpay();
  if (!ok) throw new Error("Could not load the payment gateway. Please try again.");

  return new Promise((resolve, reject) => {
    const rzp = new window.Razorpay({
      key: order.key_id,
      amount: order.amount,
      currency: order.currency,
      order_id: order.order_id,
      name: "The Pataudi",
      description: "Verified Seller Badge — one-time",
      prefill: { name: order.user?.name || "", email: order.user?.email || "", contact: order.user?.contact || "" },
      theme: { color: "#B8860B" },
      method: { upi: true, card: true, netbanking: true, wallet: true },
      handler: async (res) => {
        try {
          const { data } = await api.post("/payments/verified/verify", {
            razorpay_order_id: res.razorpay_order_id,
            razorpay_payment_id: res.razorpay_payment_id,
            razorpay_signature: res.razorpay_signature,
          });
          resolve(data);
        } catch (e) {
          reject(new Error(e.response?.data?.detail || "Payment verification failed."));
        }
      },
      modal: { ondismiss: () => reject(new Error("CANCELLED")) },
    });
    rzp.on("payment.failed", (resp) => reject(new Error(resp.error?.description || "Payment failed.")));
    rzp.open();
  });
}

/**
 * Direct pay-per-contact unlock for a single Requirement (₹199).
 * No credits, no subscription. Opens Razorpay checkout immediately.
 * Returns { phone, unlockedRequirements } on success.
 * If the caller already has free access (admin/owner/already unlocked),
 * the backend returns { free: true } and no checkout is opened.
 */
export async function unlockRequirementPayment(reqId) {
  const { data: order } = await api.post(`/requirements/${reqId}/pay/create-order`);

  // Free access — nothing to pay, unlock immediately.
  if (order.free) {
    return { phone: order.phone, unlockedRequirements: order.unlockedRequirements };
  }

  const ok = await loadRazorpay();
  if (!ok) throw new Error("Could not load the payment gateway. Please try again.");

  return new Promise((resolve, reject) => {
    const rzp = new window.Razorpay({
      key: order.key_id,
      amount: order.amount,
      currency: order.currency,
      order_id: order.order_id,
      name: "The Pataudi",
      description: "Unlock buyer contact — one number (₹199)",
      prefill: { name: order.user?.name || "", email: order.user?.email || "", contact: order.user?.contact || "" },
      theme: { color: "#166534" },
      method: { upi: true, card: true, netbanking: true, wallet: true },
      handler: async (res) => {
        try {
          const { data } = await api.post(`/requirements/${reqId}/pay/verify`, {
            razorpay_order_id: res.razorpay_order_id,
            razorpay_payment_id: res.razorpay_payment_id,
            razorpay_signature: res.razorpay_signature,
          });
          resolve(data);
        } catch (e) {
          reject(new Error(e.response?.data?.detail || "Payment verification failed."));
        }
      },
      modal: { ondismiss: () => reject(new Error("CANCELLED")) },
    });
    rzp.on("payment.failed", (resp) => reject(new Error(resp.error?.description || "Payment failed.")));
    rzp.open();
  });
}

/**
 * Purchases a seller subscription plan (adds listings to quota).
 * Returns { listingLimit, plan, added } on success.
 */
export async function purchaseSellerPlan(planId) {
  const { data: order } = await api.post("/payments/seller/create-order", { plan: planId });
  const ok = await loadRazorpay();
  if (!ok) throw new Error("Could not load the payment gateway. Please try again.");

  return new Promise((resolve, reject) => {
    const rzp = new window.Razorpay({
      key: order.key_id,
      amount: order.amount,
      currency: order.currency,
      order_id: order.order_id,
      name: "The Pataudi",
      description: `${order.plan.label} Seller Plan — +${order.plan.listings} listings`,
      prefill: { name: order.user?.name || "", email: order.user?.email || "", contact: order.user?.contact || "" },
      theme: { color: "#14532D" },
      method: { upi: true, card: true, netbanking: true, wallet: true },
      handler: async (res) => {
        try {
          const { data } = await api.post("/payments/seller/verify", {
            razorpay_order_id: res.razorpay_order_id,
            razorpay_payment_id: res.razorpay_payment_id,
            razorpay_signature: res.razorpay_signature,
          });
          resolve(data);
        } catch (e) {
          reject(new Error(e.response?.data?.detail || "Payment verification failed."));
        }
      },
      modal: { ondismiss: () => reject(new Error("CANCELLED")) },
    });
    rzp.on("payment.failed", (resp) => reject(new Error(resp.error?.description || "Payment failed.")));
    rzp.open();
  });
}
